// CallButton.jsx — click-to-call action. Rings the current user's own
// extension via Yabbit, bridges to the destination on answer. Fits in any
// list (revivable deals, GymSales journey, wasted warm leads, etc.).
import React, { useState } from 'react';
import axios from 'axios';
import { Phone, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CallButton = ({
  destination,             // "+61415597332" — required
  reason = null,           // optional short label — e.g. "revivable_deal"
  context = null,          // optional {revivable_deal_id, gymsales_lead_id, ...}
  label = 'Call',
  className = '',
  size = 'sm',             // 'sm' | 'md'
  variant = 'primary',     // 'primary' | 'ghost'
  disabled = false,
}) => {
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState('idle'); // idle | ok | error
  const [err, setErr] = useState('');

  if (!destination) return null;

  const call = async () => {
    setBusy(true); setState('idle'); setErr('');
    try {
      await axios.post(`${API}/yabbit/click-to-call`, { destination, reason, context });
      setState('ok');
      setTimeout(() => setState('idle'), 4000);
    } catch (e) {
      setState('error');
      setErr(e?.response?.data?.detail || 'Yabbit rejected the call.');
    } finally { setBusy(false); }
  };

  const sizeCls = size === 'md' ? 'px-3 py-2 text-sm' : 'px-2.5 py-1 text-xs';
  const variantCls = variant === 'primary'
    ? 'bg-teal-500 hover:bg-teal-400 disabled:opacity-50 text-white'
    : 'bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 disabled:opacity-50';
  const stateExtra = state === 'ok' ? 'bg-emerald-500 text-white' : (state === 'error' ? 'bg-red-500 text-white' : '');

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); call(); }}
      disabled={busy || disabled}
      className={`inline-flex items-center gap-1.5 rounded font-semibold transition-colors ${sizeCls} ${stateExtra || variantCls} ${className}`}
      title={state === 'error' ? err : `Click-to-call ${destination}`}
      data-testid={`call-button-${destination}`}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
        : state === 'ok' ? <CheckCircle2 className="w-3.5 h-3.5" />
        : state === 'error' ? <AlertCircle className="w-3.5 h-3.5" />
        : <Phone className="w-3.5 h-3.5" />}
      {state === 'ok' ? 'Ringing you…' : state === 'error' ? 'Failed' : label}
    </button>
  );
};

export default CallButton;
